import styled from 'styled-components';

/* Success (Welcome), dark and full-bleed. Rendered as a fixed overlay so it covers
   the layout's header and footer. */

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  overflow: auto;
  background-color: #13131f;
  font-family: 'Inter', sans-serif;
  padding: 24px 24px 48px;
`;

export const Aura = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0 0 auto 0;
  height: 760px;
  background: radial-gradient(
    58% 60% at 50% 20%,
    rgba(82, 98, 170, 0.24),
    rgba(82, 98, 170, 0) 68%
  );
`;

export const Logo = styled.img`
  position: relative;
  z-index: 1;
  height: 24px;
  width: auto;
  display: block;
  margin-left: 4px;
`;

export const Center = styled.div`
  position: relative;
  z-index: 1;
  max-width: 896px;
  margin: 0 auto;
  padding-top: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (min-width: 768px) {
    padding-top: 64px;
  }
`;

export const Win = styled.p`
  font-size: 1.6rem;
  font-weight: 600;
  color: #8b83f0;
  margin: 0;
`;

export const Headline = styled.h1`
  margin: 12px 0 0;
  font-size: 3.2rem;
  line-height: 1.12;
  font-weight: 500;
  letter-spacing: -0.02em;

  .lead {
    font-weight: 600;
    color: #fff;
  }
  .rest {
    color: rgba(255, 255, 255, 0.55);
  }

  @media (min-width: 768px) {
    font-size: 5.2rem;
  }
`;

export const Subline = styled.p`
  margin: 12px 0 0;
  font-size: 1.8rem;
  color: rgba(255, 255, 255, 0.6);

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

export const Label = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
`;

export const CodeSection = styled.div`
  margin-top: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const CodeRow = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

export const Caps = styled.div`
  display: flex;
  gap: 10px;

  @media (min-width: 640px) {
    gap: 12px;
  }
`;

export const Cap = styled.div`
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: #2a2b3b;
  font-size: 2.8rem;
  font-weight: 700;
  color: #fff;
  font-variant-numeric: tabular-nums;

  @media (min-width: 768px) {
    width: 64px;
    height: 64px;
    font-size: 3.2rem;
  }
`;

export const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 52px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: #282a3a;
  padding: 0 16px;
  font-family: 'Inter', sans-serif;
  font-size: 1.6rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: #32344a;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (min-width: 768px) {
    height: 64px;
  }
`;

export const CodeHint = styled.p`
  margin: 20px 0 0;
  font-size: 1.6rem;
  color: rgba(255, 255, 255, 0.55);
`;

/* Shown instead of the code caps when the member already has a password of their own.
   There is no access code to give them — minting one would overwrite that password. */
export const Panel = styled.div`
  margin-top: 16px;
  max-width: 560px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: #2a2b3b;
  padding: 24px;
`;

export const PanelLead = styled.p`
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
  color: #fff;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

export const PanelText = styled.p`
  margin: 12px 0 0;
  font-size: 1.6rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.6);
`;

export const Handoff = styled.div`
  margin: 48px auto 0;
  width: 100%;
  max-width: 896px;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: 48px 40px;
  justify-items: center;

  @media (min-width: 992px) {
    grid-template-columns: 1fr auto 1fr;
  }
`;

/* QR: desktop only — on mobile the person is already on their phone. */
export const QRBlock = styled.div`
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  @media (min-width: 992px) {
    display: flex;
    align-items: flex-end;
    order: 1;
  }
`;

export const QRCard = styled.div`
  border-radius: 16px;
  background: #fff;
  padding: 12px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
  line-height: 0;

  img {
    width: 124px;
    height: 124px;
    border-radius: 8px;
  }
`;

/* Mobile: mockup below the badges (order 2). Desktop: centred. */
/* Two overlapping phones: Home behind at top-left, Voucher in front at
   bottom-right. The PNGs are transparent, so the shadow follows each screen's
   rounded silhouette — no plinth. The access code is the focus; the mock is
   atmosphere. */
export const PhoneBlock = styled.div`
  order: 2;
  position: relative;
  width: 290px;
  height: 380px;
  margin: 0 auto;

  @media (min-width: 640px) {
    width: 330px;
    height: 410px;
  }

  img {
    position: absolute;
    width: 58%;
    top: 0;
    left: 0;
    filter: drop-shadow(0 24px 48px rgba(0, 0, 0, 0.5));
  }

  img + img {
    width: 62%;
    top: auto;
    left: auto;
    right: 0;
    bottom: 0;
    z-index: 1;
    filter: drop-shadow(0 28px 56px rgba(0, 0, 0, 0.55));
  }
`;

/* Mobile: badges on top (order 1). Desktop: right-hand column. */
export const BadgesBlock = styled.div`
  order: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 220px;

  @media (min-width: 992px) {
    order: 3;
    align-items: flex-start;
  }
`;

export const Badges = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  > a {
    width: 100%;
  }
`;
